import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  Warning,
  Delete,
  Cancel,
  CheckCircle
} from '@mui/icons-material';

interface UnsubscribeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userEmail?: string;
}

const UnsubscribeModal: React.FC<UnsubscribeModalProps> = ({
  open,
  onClose,
  onConfirm,
  userEmail
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [confirmations, setConfirmations] = useState([false, false, false]);
  const [processing, setProcessing] = useState(false);

  const steps = [
    'Informations importantes',
    'Conséquences du désabonnement',
    'Confirmation finale'
  ];

  const handleCheckboxChange = (index: number) => {
    const newConfirmations = [...confirmations];
    newConfirmations[index] = !newConfirmations[index];
    setConfirmations(newConfirmations);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setConfirmations([false, false, false]);
    setProcessing(false);
    onClose();
  };

  const handleFinalConfirm = async () => {
    setProcessing(true);
    try {
      // Appel immédiat de la fonction de désinscription
      await onConfirm();
      // Pas besoin de handleClose() car l'utilisateur sera déconnecté
    } catch (error) {
      console.error('Erreur lors de la désinscription:', error);
      setProcessing(false);
    }
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 0:
        return confirmations[0];
      case 1:
        return confirmations[1];
      case 2:
        return confirmations[2];
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                ⚠️ Attention : Désabonnement irréversible
              </Typography>
              <Typography>
                Vous êtes sur le point de supprimer définitivement votre compte Growlyy.
                Cette action est <strong>irréversible</strong> et entraînera la perte de toutes vos données.
              </Typography>
            </Alert>

            <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
              🗑️ Données qui seront supprimées :
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <Delete color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Tous vos leads et prospects"
                  secondary="Toute votre base de données clients sera perdue"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Delete color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Historique des RDV"
                  secondary="Tous vos rendez-vous passés et futurs"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Delete color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Campagnes et statistiques"
                  secondary="Toutes vos campagnes et leurs performances"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Delete color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Configuration et préférences"
                  secondary="Tous vos paramètres personnalisés"
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Checkbox
                checked={confirmations[0]}
                onChange={() => handleCheckboxChange(0)}
                color="error"
              />
              <Typography component="span" sx={{ ml: 1 }}>
                Je comprends que <strong>toutes mes données</strong> seront définitivement supprimées
              </Typography>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                💔 Conséquences du désabonnement
              </Typography>
              <Typography>
                Voici ce qui se passera immédiatement après la confirmation :
              </Typography>
            </Alert>

            <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
              📋 Leads en cours
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Vous avez actuellement des leads en cours de prospection. 
              En vous désabonnant, vous perdrez :
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <Warning color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Leads non contactés"
                  secondary="Tous les prospects en attente de contact"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Warning color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="RDV programmés"
                  secondary="Les RDV déjà programmés, qualifiés et effectués restent dus et seront facturés même après la désinscription."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Warning color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Suivi des prospects"
                  secondary="Impossible de continuer le suivi de vos prospects"
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Checkbox
                checked={confirmations[1]}
                onChange={() => handleCheckboxChange(1)}
                color="error"
              />
              <Typography component="span" sx={{ ml: 1 }}>
                Je comprends que je perdrai tous mes <strong>leads en cours</strong> et mes <strong>RDV programmés</strong>
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                🚨 Confirmation finale
              </Typography>
              <Typography>
                Dernière étape avant la suppression définitive de votre compte.
              </Typography>
            </Alert>

            <Box sx={{ p: 3, bgcolor: 'error.50', borderRadius: 1, mb: 3 }}>
              <Typography variant="h6" sx={{ color: 'error.main', mb: 2 }}>
                📧 Compte à supprimer :
              </Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                {userEmail || 'Non spécifié'}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
              ⚡ Actions immédiates :
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Cancel color="error" />
                </ListItemIcon>
                <ListItemText primary="Accès au dashboard supprimé immédiatement" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Cancel color="error" />
                </ListItemIcon>
                <ListItemText primary="Toutes les données supprimées définitivement" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Cancel color="error" />
                </ListItemIcon>
                <ListItemText primary="Aucune possibilité de récupération" />
              </ListItem>
            </List>

            <Alert severity="warning" sx={{ my: 2 }}>
              <Typography>
                <strong>Cette action est IRRÉVERSIBLE.</strong> Il ne sera pas possible de récupérer 
                vos données après la suppression du compte.
              </Typography>
            </Alert>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Checkbox
                checked={confirmations[2]}
                onChange={() => handleCheckboxChange(2)}
                color="error"
              />
              <Typography component="span" sx={{ ml: 1 }}>
                Je confirme vouloir <strong>supprimer définitivement</strong> mon compte et toutes mes données
              </Typography>
            </Box>

            {/* Bouton "Garder mon compte" centré */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              <Button 
                onClick={handleClose} 
                variant="contained"
                color="success"
                size="large"
                disabled={processing}
              >
                Garder mon compte
              </Button>
            </Box>

            {processing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Suppression du compte en cours...
                </Typography>
                <LinearProgress color="error" />
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={!processing ? handleClose : undefined}
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={processing}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h5" color="error.main">
          Désabonnement - Suppression du compte
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel 
                  error={index <= currentStep && !confirmations[index]}
                  StepIconProps={{
                    style: { 
                      color: confirmations[index] ? '#4caf50' : 
                             index === currentStep ? '#f44336' : '#9e9e9e'
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={processing}
          variant="outlined"
        >
          Annuler
        </Button>
        
        {currentStep > 0 && (
          <Button 
            onClick={handleBack}
            disabled={processing}
          >
            Précédent
          </Button>
        )}
        
        {currentStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext}
            disabled={!canProceedToNext() || processing}
            variant="contained"
            color="error"
          >
            Continuer
          </Button>
        ) : (
          <Button 
            onClick={handleFinalConfirm}
            disabled={!canProceedToNext() || processing}
            variant="contained"
            color="error"
            startIcon={processing ? <CircularProgress size={20} /> : <Delete />}
          >
            {processing ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UnsubscribeModal; 